// components/seo-footer-links.tsx
// SEO footer links — fully data-driven from the landing enumerator (server-fetched,
// passed as props). Only pages with real listings appear, so empty combinations
// (e.g. houses-for-rent in a city with none) never show and are naturally replaced
// by populated ones. Links render in SSR HTML, so they're crawlable.
'use client'

import Link from 'next/link'
import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import type { LandingLink } from '@/lib/seo-enumerate'

// Footer display order + a friendlier heading per enumerator group.
const GROUPS: { key: string; title: string; cap: number }[] = [
  { key: 'Browse by Type', title: 'Browse by Property Type', cap: 12 },
  { key: 'By Location', title: 'Properties by Location', cap: 40 },
  { key: 'Neighborhoods', title: 'Luxury Neighborhoods & Communities', cap: 24 },
  { key: 'Premier Developments', title: 'Premier Developments', cap: 24 },
  { key: 'Luxury', title: 'Luxury Properties', cap: 12 },
  { key: 'By Price', title: 'Browse by Price', cap: 16 },
]

export default function SEOFooterLinks({ links = [] }: { links?: LandingLink[] }) {
  const [open, setOpen] = useState<string[]>([])
  const toggle = (s: string) => setOpen((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]))

  const byGroup = new Map<string, LandingLink[]>()
  for (const l of links) {
    if (!byGroup.has(l.group)) byGroup.set(l.group, [])
    byGroup.get(l.group)!.push(l)
  }
  const sections = GROUPS.filter((g) => byGroup.get(g.key)?.length)

  if (sections.length === 0) return null

  return (
    <section className="bg-gray-50 border-t border-gray-200 py-12 md:py-16">
      <div className="container max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8">
        {sections.map(({ key, title, cap }) => {
          const items = byGroup.get(key)!.slice(0, cap)
          const isOpen = open.includes(key)
          return (
            <div key={key} className="mb-6 md:mb-10 border-b border-gray-200 md:border-0">
              <button
                onClick={() => toggle(key)}
                className="w-full md:cursor-default flex items-center justify-between py-4 md:py-0"
              >
                <h3 className="text-lg font-bold text-gray-900 md:mb-4 md:pb-2 md:border-b-2 md:border-[#d60500] text-left">
                  {title}
                </h3>
                <ChevronDown className={`w-5 h-5 text-gray-600 transition-transform md:hidden ${isOpen ? 'rotate-180' : ''}`} />
              </button>
              <div className={`${isOpen || 'hidden md:grid'} grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-1 pb-4 md:pb-0`}>
                {items.map((l) => (
                  <Link
                    key={l.slug}
                    href={`/${l.slug}`}
                    className="block py-2 text-sm text-gray-600 hover:text-[#d60500] hover:underline"
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>
          )
        })}
        <div className="pt-2">
          <Link href="/browse" className="text-sm font-semibold text-[#d60500] hover:underline">
            Browse all property categories →
          </Link>
        </div>
      </div>
    </section>
  )
}
